import { CustomSelectField } from '@components';
import {
  Box,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import CustomTextField from '../../../../components/CustomTextField/CustomTextField';
import { PvxPartnerDetailsFormData } from '../types/partnerDetails';
import { FC, useMemo, useState } from 'react';
import {
  PARTNER_ENTITY_TYPE_DISPLAY_LABELS,
  PARTNER_ENTITY_TYPE_OPTIONS,
  PARTNER_TYPE_DISPLAY_LABELS,
  PARTNER_TYPE_FILTER_OPTIONS,
  PARTNER_TYPE_OPTIONS,
} from '../constants';
import { PVX_PARTNERS_TYPE } from '@api/types/pvxPartners';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGetPvxPocQuery } from '@api/games';
import { FormErrors, TouchedFields } from '../hooks/usePvxPartnerDetailsForm';

const formSectionTitleStyles = { fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' };
const gridContainerStyles = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
  marginBottom: '1rem',
};
const labelStyles = {
  fontSize: '0.625rem',
  fontWeight: 700,
  lineHeight: '1rem',
  color: '#6C6C6C',
};
const valueStyles = {
  fontSize: '1rem',
  fontWeight: 700,
  lineHeight: '1.375rem',
  color: '#162936',
};
const cardStyles = {
  backgroundColor: '#fff',
  borderRadius: '0.5rem',
  border: '1px solid #E8EAEB',
  padding: '1.25rem 1.5rem',
  marginBottom: '1.5rem',
};

const ReadOnlyField: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
    <Typography sx={labelStyles}>{label}</Typography>
    <Typography sx={valueStyles}>{value || '-'}</Typography>
  </Box>
);

const PartnerDetailsAccordion: FC<{
  partnerDetails: PvxPartnerDetailsFormData;
  isEditing: boolean;
  handleEditClick: () => void;
}> = ({ partnerDetails, isEditing, handleEditClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box sx={cardStyles}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: isExpanded ? '1.25rem' : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Typography fontSize="1rem" fontWeight={700} sx={{ color: '#162936' }}>
            Partner Details
          </Typography>
          {!isEditing && (
            <IconButton
              disableRipple
              size="small"
              onClick={handleEditClick}
              aria-label="Edit partner details"
              sx={{ p: 0, color: '#51B8B0' }}
            >
              <EditOutlinedIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          )}
        </Box>
        <ExpandMoreIcon
          onClick={() => setIsExpanded((prev) => !prev)}
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease-in-out',
            cursor: 'pointer',
            color: '#162936',
          }}
          aria-expanded={isExpanded}
        />
      </Box>
      <Collapse in={isExpanded} timeout={200} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <Box sx={gridContainerStyles}>
            <ReadOnlyField
              label="Type"
              value={
                PARTNER_ENTITY_TYPE_DISPLAY_LABELS[partnerDetails.entityType] ??
                partnerDetails.entityType
              }
            />
            <ReadOnlyField
              label="Partner Type"
              value={
                PARTNER_TYPE_DISPLAY_LABELS[partnerDetails.partnerType as PVX_PARTNERS_TYPE] ??
                partnerDetails.partnerType
              }
            />
            <ReadOnlyField label={'Company Name'} value={partnerDetails.name} />
            <ReadOnlyField label={'Company Address'} value={partnerDetails.address ?? ''} />
            <ReadOnlyField label={'POC Name'} value={partnerDetails.username ?? ''} />
            <ReadOnlyField label={'POC Email'} value={partnerDetails.email} />
          </Box>
          <Divider sx={{ borderColor: '#E8EAEB' }} />
          <ReadOnlyField label="PvX POC" value={partnerDetails.pvxPocEmail ?? '-'} />
        </Box>
      </Collapse>
    </Box>
  );
};

type PartnerDetailsFormProps = {
  partnerDetails: PvxPartnerDetailsFormData;
  touchedFields: TouchedFields;
  formErrors: FormErrors;
  handleFieldChange: (field: keyof PvxPartnerDetailsFormData, value: string) => void;
  handleFieldBlur: (field: keyof PvxPartnerDetailsFormData) => void;
  isEditMode: boolean;
};

const PartnerDetailsForm: FC<PartnerDetailsFormProps> = ({
  partnerDetails,
  touchedFields,
  formErrors,
  handleFieldChange,
  handleFieldBlur,
  isEditMode,
}) => {
  const { data: pvxPocListData, isLoading: isPvxPocLoading } = useGetPvxPocQuery();

  const pvxPocOptions = useMemo(() => {
    return (
      pvxPocListData?.data?.map((poc) => ({
        label: `${poc.ownerName} - (${poc.emailId})`,
        value: poc.emailId,
      })) ?? []
    );
  }, [pvxPocListData]);

  const getFieldError = (field: keyof PvxPartnerDetailsFormData): string =>
    touchedFields[field] ? (formErrors[field] ?? '') : '';

  return (
    <Box>
      <Typography sx={formSectionTitleStyles}>Partner Details</Typography>
      <Box sx={gridContainerStyles}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControl fullWidth required error={!!getFieldError('entityType')}>
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              id="type"
              value={partnerDetails.entityType}
              label="Type"
              onChange={(e) => handleFieldChange('entityType', e.target.value)}
              onBlur={() => handleFieldBlur('entityType')}
            >
              {PARTNER_ENTITY_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {getFieldError('entityType') && (
            <Typography variant="caption" color="error">
              {getFieldError('entityType')}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={gridContainerStyles}>
        <CustomTextField
          id="company-name"
          label={'Company Name'}
          value={partnerDetails.name}
          onFieldChange={(e) => handleFieldChange('name', e.target.value)}
          onBlur={() => handleFieldBlur('name')}
          error={!!getFieldError('name')}
          helperText={getFieldError('name')}
          required
        />
        <CustomTextField
          id="company-address"
          label={'Company Address'}
          value={partnerDetails.address ?? ''}
          onFieldChange={(e) => handleFieldChange('address', e.target.value)}
          onBlur={() => handleFieldBlur('address')}
        />
        <CustomTextField
          id="poc-name"
          label="POC Name"
          value={partnerDetails.username}
          onFieldChange={(e) => handleFieldChange('username', e.target.value)}
          onBlur={() => handleFieldBlur('username')}
          error={!!getFieldError('username')}
          helperText={getFieldError('username')}
          required
          disabled={isEditMode}
        />
        <CustomTextField
          id="poc-email"
          label={'POC Email'}
          value={partnerDetails.email}
          onFieldChange={(e) => handleFieldChange('email', e.target.value)}
          onBlur={() => handleFieldBlur('email')}
          error={!!getFieldError('email')}
          helperText={getFieldError('email')}
          required
          disabled={isEditMode}
        />
      </Box>

      {/* Partner Type */}
      <Typography sx={formSectionTitleStyles}>Partner Type</Typography>
      <Box sx={gridContainerStyles}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControl
            fullWidth
            required
            error={!!getFieldError('partnerType')}
            disabled={isEditMode}
          >
            <InputLabel id="partner-type-label">Partner Type</InputLabel>
            <Select
              labelId="partner-type-label"
              id="partner-type"
              value={partnerDetails.partnerType}
              label="Partner Type"
              onChange={(e) => handleFieldChange('partnerType', e.target.value)}
              onBlur={() => handleFieldBlur('partnerType')}
            >
              {(isEditMode ? PARTNER_TYPE_FILTER_OPTIONS : PARTNER_TYPE_OPTIONS).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {getFieldError('partnerType') && (
            <Typography variant="caption" color="error">
              {getFieldError('partnerType')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* PvX POC */}
      <Typography sx={formSectionTitleStyles}>PvX POC</Typography>
      <Box sx={gridContainerStyles}>
        <CustomSelectField
          labelId="pvx-poc"
          label="PvX POC"
          options={pvxPocOptions}
          disabled={isPvxPocLoading}
          selectedValue={partnerDetails.pvxPocEmail ?? ''}
          onChange={(value) => handleFieldChange('pvxPocEmail', value as string)}
          onBlur={() => handleFieldBlur('pvxPocEmail')}
          isSearchable
        />
      </Box>
    </Box>
  );
};

type PvxPartnerDetailsSectionProps = {
  partnerDetails: PvxPartnerDetailsFormData;
  touchedFields: TouchedFields;
  formErrors: FormErrors;
  handleFieldChange: (field: keyof PvxPartnerDetailsFormData, value: string) => void;
  handleFieldBlur: (field: keyof PvxPartnerDetailsFormData) => void;
  isEditMode: boolean;
};

export const PvxPartnerDetailsSection = ({
  partnerDetails,
  touchedFields,
  formErrors,
  handleFieldChange,
  handleFieldBlur,
  isEditMode,
}: PvxPartnerDetailsSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);

  return isEditMode && !isEditing ? (
    <PartnerDetailsAccordion
      partnerDetails={partnerDetails}
      isEditing={isEditing}
      handleEditClick={() => setIsEditing(true)}
    />
  ) : (
    <PartnerDetailsForm
      partnerDetails={partnerDetails}
      touchedFields={touchedFields}
      formErrors={formErrors}
      handleFieldChange={handleFieldChange}
      handleFieldBlur={handleFieldBlur}
      isEditMode={isEditMode}
    />
  );
};
